/**
 * TrialExpired — aligned to Landing v2 aesthetic (ivory base, navy card,
 * gold CTA, Cabinet Grotesk display font). Forces light theme.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LockKeyhole, ArrowRight, Mail, Clock, CheckCircle2, Zap } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_lead-discovery-hub-3/artifacts/wg4n305a_AgenteLead%20Pro%20Logo.png";

// Iter98 — same self-defence set used in GlobalTrialBanner. The trial-expired
// screen must NEVER render for the platform owner, even if a stale JWT carries
// role=user against the canonical master email.
const HARDCODED_MASTERS = new Set([
  "master@agentelead.pro",
  "master-demo@agentelead.pro",
]);
const ADMIN_ROLES = new Set(["super_admin", "admin", "master", "master_demo"]);
const isAdminUser = (u) =>
  !!u && (ADMIN_ROLES.has(u.role) || HARDCODED_MASTERS.has((u.email || "").toLowerCase()));

export default function TrialExpired() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [trial, setTrial] = useState(null);

  // Iter98 — immediate admin escape hatch. Run BEFORE the network call so the
  // master/master_demo can never land on this screen.
  useEffect(() => {
    if (isAdminUser(user)) {
      navigate("/master", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const root = document.documentElement;
    const prev = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "light");
    let token = "";
    try { token = window.localStorage.getItem("alp.access_token") || ""; } catch (_e) { /* ignore */ }
    if (token) {
      axios.get(`${API}/trial/status`).then(({ data }) => setTrial(data)).catch(() => {});
    }
    return () => { if (prev !== null) root.setAttribute("data-theme", prev); else root.removeAttribute("data-theme"); };
  }, []);

  useEffect(() => {
    if (trial && !trial.expired && !trial.is_admin) navigate("/", { replace: true });
    if (trial?.is_admin) navigate("/", { replace: true });
  }, [trial, navigate]);

  // Iter98 — early return: if the JWT says admin OR email is canonical
  // master, render NOTHING (we redirected above; this prevents a 1-frame
  // flash of the locked screen during the navigate).
  if (isAdminUser(user)) return null;

  const onLogout = async () => { await logout(); window.location.assign("/login"); };

  return (
    <div data-testid="trial-expired-page"
      className="min-h-screen flex items-center justify-center p-6 font-body antialiased"
      style={{ background: "#FBF9F4", color: "#0F172A" }}>
      <div className="pointer-events-none fixed inset-0 -z-10" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(15,23,42,0.04) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-2.5 mb-6">
          <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
          <span className="font-display text-[18px] font-extrabold tracking-tight" style={{ color: "#0A1233" }}>AgenteLead<span style={{ color: "#C9A24E" }}>.Pro</span></span>
        </div>
        <div className="rounded-2xl shadow-xl border-2 p-8 lg:p-10" style={{ backgroundColor: "#FFFFFF", borderColor: "#FECACA" }}>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-black uppercase tracking-[0.18em]" style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}>
            <LockKeyhole className="w-3 h-3" /> {isPt ? "Trial expirado" : "Trial expired"}
          </div>
          <h1 className="font-display mt-5 text-3xl md:text-4xl font-black tracking-tight leading-[1.05]" style={{ color: "#0A1233" }}>
            {isPt ? "Seu período de avaliação terminou." : "Your trial period has ended."}
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#475569" }}>
            {isPt
              ? "Todos os seus leads, integrações e configurações continuam salvos. Escolha um plano abaixo e em segundos sua conta volta ao ar exatamente de onde parou."
              : "Your leads, integrations and configurations are all preserved. Pick a plan below and your account is back online — exactly where you left off."}
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
            {[
              { i: Zap, t: isPt ? "2 min para reativar" : "2 min to reactivate" },
              { i: CheckCircle2, t: isPt ? "Sem perda de dados" : "Zero data loss" },
            ].map((f) => {
              const Icon = f.i;
              return (
                <div key={f.t} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ backgroundColor: "#F5F2EA", borderColor: "#E5E2DA" }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: "#C9A24E" }} /> <span className="text-[12px] font-semibold" style={{ color: "#0A1233" }}>{f.t}</span>
                </div>
              );
            })}
          </div>

          {trial && (
            <div className="mt-5 text-[11.5px] rounded-lg p-3 border space-y-0.5" style={{ backgroundColor: "#FAFAF7", borderColor: "#E5E2DA", color: "#475569" }}>
              <div><b>Email:</b> {user?.email || trial.email}</div>
              <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> <b>{isPt ? "Início" : "Started"}:</b> {(trial.first_login_at || "").slice(0, 10)}</div>
              <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> <b>{isPt ? "Fim" : "Ended"}:</b> {(trial.trial_end_at || "").slice(0, 10)}</div>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
            <button type="button" data-testid="trial-expired-upgrade" onClick={() => navigate("/billing")}
              className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-lg font-bold text-[14px] transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: "#C9A24E", color: "#0A1233", boxShadow: "0 8px 30px -10px rgba(201,162,78,0.55)" }}>
              {isPt ? "Escolher plano" : "Choose plan"} · UPGRADE <ArrowRight className="w-4 h-4" />
            </button>
            <a href="mailto:contato@agentelead-pro.com?subject=Suporte%20-%20Trial%20Expirado"
              data-testid="trial-expired-support"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-lg border font-semibold text-[13px] transition-colors"
              style={{ borderColor: "#E5E2DA", color: "#475569" }}>
              <Mail className="w-4 h-4" /> {isPt ? "Falar com suporte" : "Contact support"}
            </a>
          </div>

          <div className="mt-6 pt-4 border-t flex items-center justify-between" style={{ borderColor: "#E5E2DA" }}>
            <span className="text-[11.5px]" style={{ color: "#94A3B8" }}>
              {isPt ? "Já tem outra conta? Faça logout para alternar." : "Have another account? Log out to switch."}
            </span>
            <button type="button" data-testid="trial-expired-logout" onClick={onLogout}
              className="text-[11.5px] underline" style={{ color: "#64748B" }}>
              {isPt ? "Sair" : "Log out"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
